/**
 * 샵관리 공통 그리드 화면 골격 (조회 / 추가 / 삭제 / 저장 / 엑셀)
 * 개별 화면은 cfg 만 넘긴다. 저장소 접근은 shop/shopStore 를 통한다.
 *
 * cfg:
 *  gridId, title, uidKey
 *  columns(ctx)            ctx = { tc, reload }
 *  load(tc)                전체 행 (파생 컬럼 포함 가능)
 *  filter(rows, values)    조회조건 적용
 *  emptyRow(rows, tc)      신규 행 기본값
 *  validate(rows, tc)      string[] 오류 (삭제 행 포함, mod === 'D')
 *  save(tc, rows, values)  저장 (예외 = 저장 실패 메시지)
 *  searchFields(ctx), searchDefaults
 *  readOnly                조회 전용(추가/삭제/저장 숨김)
 *  noAdd                   추가 버튼만 숨김(삭제·저장 유지)
 *  renderTop({tc, research, reload})  조회조건 위 입력영역
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';

const Inner = ({ cfg }) => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tc = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tc, user), [tc, user]);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const searchDefaultValues = useMemo(() => ({ ...(cfg.searchDefaults || {}) }), [cfg.searchDefaults]);
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaultValues });
  const { getValues: getSearchValues } = methods;

  // 콤보 옵션은 마운트/재조회 시점 스토어 값으로 만든다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => cfg.columns({ tc, reload }), [tc, reloadKey]);
  const search = useCallback(
    () => cfg.filter(cfg.load(tc), getSearchValues() || {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tc, cfg, getSearchValues]
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues,
    enableContextMenu: true,
    autoSearch: false,
    confirmCheck: true,
    defaultRow: cfg.emptyRow ? cfg.emptyRow([], tc) : {},
    getDefaultRow: () => cfg.emptyRow?.(gridRef.current?.getData?.() ?? [], tc) ?? {},
    customOnSearch: async () => search(),
    customOnSave: async ({ setData }) => {
      const current = gridRef.current?.getData?.() ?? [];
      const errors = cfg.validate?.(current, tc) || [];
      if (errors.length) {
        showAlert(errors.slice(0, 8).join('\n'), 'warning');
        return;
      }
      try {
        await cfg.save(tc, current, getSearchValues() || {});
      } catch (e) {
        showAlert(e.message || '저장 실패', 'error');
        return;
      }
      setData(search());
      reload();
      showAlert('저장했습니다.', 'success');
    },
  });

  useEffect(() => {
    grid.handleSearch().catch((e) => showAlert(e.message || '조회 실패', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 기관 변경 시 재조회
  }, [tc]);

  const getData = useCallback(() => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [], []);
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);
  useRegisterGridExport({ id: cfg.gridId, label: cfg.title, getData, getDataColumns, order: 0 });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: cfg.title });

  const onError = (e) => showAlert(e.message || '처리 실패', 'error');
  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: () => grid.handleSearch().catch(onError), hotkey: 'F7' },
    ...(cfg.readOnly
      ? []
      : [
          ...(cfg.noAdd ? [] : [{ id: 'add', label: '추가', icon: '➕', onClick: () => grid.handleAdd(), hotkey: 'F8' }]),
          { id: 'delete', label: '삭제', icon: '➖', onClick: () => grid.handleDelete(), hotkey: 'F9' },
          { id: 'save', label: '저장', icon: '💾', onClick: () => grid.handleSave().catch(onError), hotkey: 'F10' },
        ]),
    excelButton,
  ];

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        {cfg.renderTop?.({
          tc,
          reload,
          research: () => grid.handleSearch().catch(onError),
        })}
        {cfg.searchFields && (
          <CollapsibleSearchPanel storageKey={cfg.gridId} prefsUserId={prefsUserId} title="조회조건">
            <FormArea
              fields={cfg.searchFields({ tc })}
              compact
              onFieldEnter={() => grid.handleSearch()}
              defaultValues={searchDefaultValues}
            />
          </CollapsibleSearchPanel>
        )}
      </FormProvider>
      <GridExportFocusScope gridId={cfg.gridId}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu
            enableCut
            enableFindReplace
            allowReplace
            enableHeaderMenu
            enableSelectionStats
            enableFreeze
            enableQuickExport
            showModColumn={!cfg.readOnly}
            exportFileName={cfg.title}
            gridId={cfg.gridId}
            prefsUserId={prefsUserId}
            {...grid.contextMenuProps}
          />
        </div>
      </GridExportFocusScope>
      {excelDialog}
    </PageContainer>
  );
};

const ShopGridPage = ({ cfg }) => (
  <GridExportProvider>
    <Inner cfg={cfg} />
  </GridExportProvider>
);

export default ShopGridPage;
